import { ref, computed, getCurrentInstance, onBeforeUnmount } from "vue";
import type { ComputedRef } from "vue";
import store from "@/store";
import i18n from "@/i18n";
import { DataModules } from "@/store/modules/data/modules";
import {
  CustomFieldsMajorTypes,
  CustomFieldsTypes,
  CustomFieldsFilterTypes
} from "@/data/enums/customFields";
import { FilterTypes } from "@/data/table";
import { f } from "./filters";
import type { FilterDef } from "./types";
import type { ICustomField } from "@upmind-automation/types";

export interface UseCustomFieldsReturn {
  /** Tracked custom field lists by major type. */
  basketFields: ComputedRef<ICustomField[]>;
  clientFields: ComputedRef<ICustomField[]>;
  cancellationFields: ComputedRef<ICustomField[]>;
  /** True while any of the three fetches is in-flight. */
  isLoading: ComputedRef<boolean>;
  fetchBasket: (
    ignoreStored?: boolean,
    withStagedImports?: boolean
  ) => Promise<void>;
  fetchClient: (
    ignoreStored?: boolean,
    withStagedImports?: boolean
  ) => Promise<void>;
  fetchCancellation: (ignoreStored?: boolean) => Promise<void>;
  /**
   * Build FilterDef[] directly from the loaded custom fields. Replaces the
   * legacy IFilter → legacyToDef pipeline.
   */
  filtersFor: (
    majorType: CustomFieldsMajorTypes,
    prefix: string,
    group: string,
    ignoreLabel?: boolean
  ) => FilterDef[];
}

/**
 * Listing-side counterpart to CustomFieldsMixin.
 *
 * Mirrors `getCustomBasketFields` / `getCustomClientFields` /
 * `customFieldsFilters` for use in Composition API setup blocks. Reads from
 * the same Vuex `data/custom_fields` store, so existing mixin consumers are
 * unaffected. Cleans up scopes on unmount.
 *
 * Unlike the mixin's `customFieldsFilters` (which returns IFilter[]), this
 * composable's `filtersFor` returns `FilterDef[]` directly so callers no
 * longer need a legacy → def shim.
 */
export function useCustomFields(): UseCustomFieldsReturn {
  const vm = getCurrentInstance()?.proxy;

  const isFetchingBasket = ref(false);
  const isFetchingClient = ref(false);
  const isFetchingCancellation = ref(false);

  const basketScope = computed<string>(() =>
    store.getters[`data/${DataModules.CUSTOM_FIELDS}/listScope`](
      CustomFieldsMajorTypes.INVOICE
    )
  );
  const clientScope = computed<string>(() =>
    store.getters[`data/${DataModules.CUSTOM_FIELDS}/listScope`](
      CustomFieldsMajorTypes.CLIENT
    )
  );
  const cancellationScope = computed<string>(() =>
    store.getters[`data/${DataModules.CUSTOM_FIELDS}/listScope`](
      CustomFieldsMajorTypes.CANCEL_REQUEST
    )
  );

  const basketFields = computed<ICustomField[]>(
    () =>
      store.getters[`data/listArray`]({
        scope: basketScope.value,
        storeModule: DataModules.CUSTOM_FIELDS
      }) || []
  );
  const clientFields = computed<ICustomField[]>(
    () =>
      store.getters[`data/listArray`]({
        scope: clientScope.value,
        storeModule: DataModules.CUSTOM_FIELDS
      }) || []
  );
  const cancellationFields = computed<ICustomField[]>(
    () =>
      store.getters[`data/listArray`]({
        scope: cancellationScope.value,
        storeModule: DataModules.CUSTOM_FIELDS
      }) || []
  );

  const isLoading = computed(
    () =>
      isFetchingBasket.value ||
      isFetchingClient.value ||
      isFetchingCancellation.value
  );

  const baseParams = { limit: 0, order: "order" };

  async function fetchBasket(ignoreStored = false, withStagedImports = false) {
    isFetchingBasket.value = true;
    try {
      await store.dispatch(`data/${DataModules.CUSTOM_FIELDS}/list`, {
        customFieldsType: CustomFieldsMajorTypes.INVOICE,
        scope: basketScope.value,
        params: {
          ...baseParams,
          ...(withStagedImports ? { with_staged_imports: 1 } : {})
        },
        ignoreStored,
        vm
      });
    } finally {
      isFetchingBasket.value = false;
    }
  }

  async function fetchClient(ignoreStored = false, withStagedImports = false) {
    isFetchingClient.value = true;
    try {
      await store.dispatch(`data/${DataModules.CUSTOM_FIELDS}/list`, {
        customFieldsType: CustomFieldsMajorTypes.CLIENT,
        scope: clientScope.value,
        params: {
          ...baseParams,
          ...(withStagedImports ? { with_staged_imports: 1 } : {})
        },
        ignoreStored,
        vm
      });
    } finally {
      isFetchingClient.value = false;
    }
  }

  async function fetchCancellation(ignoreStored = false) {
    isFetchingCancellation.value = true;
    try {
      await store.dispatch(`data/${DataModules.CUSTOM_FIELDS}/list`, {
        customFieldsType: CustomFieldsMajorTypes.CANCEL_REQUEST,
        scope: cancellationScope.value,
        params: baseParams,
        ignoreStored,
        vm
      });
    } finally {
      isFetchingCancellation.value = false;
    }
  }

  function fieldKey(cf: ICustomField, prefix: string): string {
    return `${prefix ? prefix + "." : ""}custom_fields.${cf.code}`;
  }

  function fieldLabel(
    majorType: CustomFieldsMajorTypes,
    label: string,
    ignore: boolean
  ): string {
    if (ignore) return label;
    if (majorType === CustomFieldsMajorTypes.BASKET) {
      return String(i18n.t("_.order_field", { field: label }));
    }
    if (majorType === CustomFieldsMajorTypes.CANCEL_REQUEST) {
      return String(i18n.t("_.cancellation_field", { field: label }));
    }
    return String(i18n.t("_.client_field", { field: label }));
  }

  function buildDef(
    cf: ICustomField,
    key: string,
    label: string,
    group: string
  ): FilterDef | null {
    const filterType = CustomFieldsFilterTypes[cf.type as CustomFieldsTypes];

    if (filterType === FilterTypes.SELECT) {
      const opts = (cf.values || []) as { value: string; label: string }[];
      return f.select(key, {
        label,
        group,
        options: opts.map(o => ({
          value: String((o as { value?: unknown }).value ?? o),
          label: String((o as { label?: unknown }).label ?? o)
        }))
      });
    }
    if (filterType === FilterTypes.SELECT_RADIO) {
      return f.select(key, {
        label,
        group,
        multi: false,
        options: [
          { value: "1", label: String(i18n.t("_.yes")) },
          { value: "0", label: String(i18n.t("_.no")) }
        ]
      });
    }
    if (filterType === FilterTypes.DATE) {
      return f.date(key, { label, group });
    }
    if (filterType === FilterTypes.NUMBER) {
      return f.number(key, { label, group });
    }
    if (filterType === FilterTypes.STRING) {
      return f.text(key, { label, group });
    }
    return null;
  }

  function filtersFor(
    majorType: CustomFieldsMajorTypes,
    prefix: string,
    group: string,
    ignoreLabel = false
  ): FilterDef[] {
    const source =
      majorType === CustomFieldsMajorTypes.CLIENT
        ? clientFields.value
        : majorType === CustomFieldsMajorTypes.BASKET
        ? basketFields.value
        : cancellationFields.value;

    const out: FilterDef[] = [];
    for (const cf of source) {
      if (cf.type === CustomFieldsTypes.IMAGE) continue;
      const key = fieldKey(cf, prefix);
      const label = fieldLabel(
        majorType,
        cf.name_translated as unknown as string,
        ignoreLabel
      );
      const def = buildDef(cf, key, label, group);
      if (def) out.push(def);
    }
    return out;
  }

  onBeforeUnmount(() => {
    store.dispatch("data/binList", {
      scope: clientScope.value,
      storeModule: DataModules.CUSTOM_FIELDS,
      vm
    });
    store.dispatch("data/binList", {
      scope: basketScope.value,
      storeModule: DataModules.CUSTOM_FIELDS,
      vm
    });
    store.dispatch("data/binList", {
      scope: cancellationScope.value,
      storeModule: DataModules.CUSTOM_FIELDS,
      vm
    });
  });

  return {
    basketFields,
    clientFields,
    cancellationFields,
    isLoading,
    fetchBasket,
    fetchClient,
    fetchCancellation,
    filtersFor
  };
}
